import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Coins, FileText, Users, ArrowRight, AlertTriangle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import SocialProjectsBanner from '@/components/SocialProjectsBanner';
import axios from 'axios';
import { isBusiness } from '@/lib/roles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restaurant owners must not land on the user Dashboard
    if (isBusiness(user)) {
      navigate('/restaurant/dashboard', { replace: true });
      return;
    }
    (async () => {
      try { const res = await axios.get(`${API}/users/dashboard`, { withCredentials: true }); setStats(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user, navigate]);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;

  const kpis = [
    { label: t('dash_reviews'), value: stats?.reviews_written || 0, icon: <FileText className="w-5 h-5" strokeWidth={1.5} />, color: "text-primary", link: "/my-reviews" },
    { label: t('dash_times_used'), value: stats?.times_used || 0, icon: <Users className="w-5 h-5" strokeWidth={1.5} />, color: "text-secondary-foreground", link: "/my-reviews" },
    { label: t('dash_coins_earned'), value: stats?.coins_earned?.toFixed(2) || "0.00", icon: <Coins className="w-5 h-5" strokeWidth={1.5} />, color: "text-emerald-600", link: "/wallet" },
  ];

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="user-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">{t('nav_dashboard')}</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t('dash_title')}, {user?.name?.split(' ')[0]}
          </h1>
        </div>

        {/* Phase 24 — Social Projects banner (always visible on consumer dashboard) */}
        <div className="mb-6">
          <SocialProjectsBanner />
        </div>

        {/* Balance Card */}
        <Card className="border border-border rounded-2xl mb-6 bg-gradient-to-br from-card to-secondary/30 cursor-pointer card-hover" onClick={() => navigate('/wallet')} data-testid="balance-card">
          <CardContent className="p-5 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1 font-sans">{t('dash_balance')}</p>
            <p className="text-3xl sm:text-4xl font-bold text-foreground" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {stats?.current_balance?.toFixed(2) || "0.00"} <span className="text-base sm:text-lg text-muted-foreground">{t('dash_coins')}</span>
            </p>
          </CardContent>
        </Card>

        {/* Coin Expiry Warning */}
        {stats?.coins_expiring_30d > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-5 mb-6 flex items-start gap-3 cursor-pointer card-hover" onClick={() => navigate('/wallet')} data-testid="coins-expiring-box">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-semibold font-sans text-amber-800">{t('dash_coins_expiring')}</p>
              <p className="text-2xl font-bold text-amber-700 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {stats.coins_expiring_30d} coin
              </p>
            </div>
          </div>
        )}

        {/* KPI Grid — clickable */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
          {kpis.map((kpi, i) => (
            <Card key={kpi.label} className="border border-border rounded-2xl card-hover cursor-pointer" onClick={() => navigate(kpi.link)} data-testid={`kpi-${i}`}>
              <CardContent className="p-4 sm:p-6">
                <div className={`w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-muted flex items-center justify-center ${kpi.color} mb-2 sm:mb-3`}>{kpi.icon}</div>
                <p className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{kpi.value}</p>
                <p className="text-[11px] sm:text-xs text-muted-foreground font-sans mt-1">{kpi.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Action — Browse Restaurants only */}
        <Card className="border border-border rounded-2xl card-hover cursor-pointer" onClick={() => navigate('/restaurants')} data-testid="action-browse">
          <CardContent className="p-5 sm:p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0"><Users className="w-6 h-6" strokeWidth={1.5} /></div>
            <div className="flex-1 min-w-0"><p className="font-semibold text-sm font-sans">{t('dash_browse')}</p><p className="text-xs text-muted-foreground font-sans">{t('dash_browse_d')}</p></div>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        {stats?.recent_transactions?.length > 0 && (
          <Card className="border border-border rounded-2xl mt-6" data-testid="recent-transactions">
            <CardHeader><CardTitle className="text-base sm:text-lg font-sans font-semibold">{t('dash_recent')}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_transactions.map((tx, i) => {
                  const EARN_TYPES = new Set(['earn', 'REVIEW_AUTHOR_REWARD', 'REFERRER_REWARD', 'refund']);
                  const isEarn = EARN_TYPES.has(tx.type);
                  const isUsed = tx.type === 'used' || tx.type === 'COIN_DISCOUNT';
                  return (
                    <div key={tx.transaction_id || i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div className="min-w-0 flex-1"><p className="text-sm font-sans font-medium truncate">{tx.description}</p><p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</p></div>
                      <Badge className={`shrink-0 ml-2 text-xs ${isEarn ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : isUsed ? 'bg-primary/10 text-primary border-primary/20' : 'bg-muted text-muted-foreground border-border'}`}>
                        {isEarn ? '+' : '-'}{tx.amount} coin
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
